import { BadgeCheck, FileCheck2, TriangleAlert } from "lucide-react";
import type { ReactNode } from "react";
import { Input } from "@flint/ui";
import { Field, SectionCard } from "./section-card.js";
import type { NewBillFormValues } from "./types.js";

interface MatchSectionProps {
  values: NewBillFormValues;
  onChange: <K extends keyof NewBillFormValues>(field: K, value: NewBillFormValues[K]) => void;
}

export function MatchSection({ values, onChange }: MatchSectionProps) {
  return (
    <SectionCard
      step="3"
      title="Reference & match"
      description="Link this bill to a purchase order and goods-receipt note. 3-way match is the founder-approval shortcut."
    >
      <div className="grid gap-3 md:grid-cols-2">
        <Field id="poNumber" label="Purchase order">
          <Input
            id="poNumber"
            value={values.poNumber}
            onChange={(event) => onChange("poNumber", event.target.value)}
            placeholder="PO-2026-038"
            className="font-mono"
          />
          <p className="text-xs font-medium text-ok">PO found · ₹1.40L · same vendor</p>
        </Field>
        <Field id="grnNumber" label="Goods receipt note (GRN)">
          <Input
            id="grnNumber"
            value={values.grnNumber}
            onChange={(event) => onChange("grnNumber", event.target.value)}
            placeholder="GRN-044"
            className="font-mono"
          />
          <p className="text-xs font-medium text-ok">GRN logged 02 May · 100% qty · QC passed</p>
        </Field>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-3">
        <MatchTile
          icon={<BadgeCheck className="h-4 w-4" />}
          label="PO ↔ bill match (2-way)"
          value="PO-2026-038 · ₹1.40L · same vendor + same SKUs"
          meta="All 4 SKUs match; quantities and unit prices are within tolerance."
          tone="ok"
        />
        <MatchTile
          icon={<FileCheck2 className="h-4 w-4" />}
          label="GRN ↔ bill match"
          value={values.grnNumber ? "GRN-044 · all quantities received" : "Awaiting GRN"}
          meta="QC passed by Priya. 3-way match unlocks faster approval."
          tone={values.grnNumber ? "ok" : "warn"}
        />
        <MatchTile
          icon={<TriangleAlert className="h-4 w-4" />}
          label="Review flag"
          value="1 price discrepancy"
          meta="Spring washers show ₹15 instead of PO ₹14."
          tone="warn"
        />
      </div>
    </SectionCard>
  );
}

function MatchTile({
  icon,
  label,
  value,
  meta,
  tone,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  meta: string;
  tone: "ok" | "warn";
}) {
  return (
    <div
      className={`flex gap-3 rounded-md border p-3 ${tone === "ok" ? "border-ok-100 bg-ok-50" : "border-warn-100 bg-warn-50"}`}
    >
      <div
        className={`grid h-8 w-8 place-items-center rounded-full ${tone === "ok" ? "bg-paper text-ok" : "bg-paper text-warn"}`}
      >
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-ink">{label}</p>
        <p className="mt-1 text-xs text-ink-2">{value}</p>
        <p className="mt-1 text-xs text-muted">{meta}</p>
      </div>
    </div>
  );
}
